import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { C, fmtINR } from "./ipd-billing/constants";
import SvgIcon from "./ipd-billing/SvgIcon";
import IPDBillingLayout from "./ipd-billing/components/IPDBillingLayout";
import AdmissionPayoutWorkflow from "./ipd-billing/components/AdmissionPayoutWorkflow";
import {
  ENC_OPTIONS,
  todayISO,
  defaultPayPeriod,
  formatPayCycleLabel,
  calculateDoctorPayouts,
  getPayoutDashboard,
  getPayoutInsights,
  getActivePayoutRules,
  getProviderPayoutDetail,
  exportPayoutRegister,
  getPayoutRunPreview,
  getPayoutApprovers,
  createPayoutRunWizard,
  buildWizardPayload,
  downloadPayoutBankFile,
  downloadPayoutTdsRegister,
  generatePayoutPayslips,
  listPayoutRuns,
  getPayoutRunDetail,
  approvePayoutRun,
  disbursePayoutRun,
  holdPayoutLine,
  releasePayoutLine,
  RUN_STATUS_TONE,
} from "./ipd-billing/payout-api";

// ─── style helpers ────────────────────────────────────────────────────────────
const ENC_BADGE = {
  OPD:       { bg: "#dbeafe", color: "#1e40af", border: "#93c5fd" },
  IPD:       { bg: "#e0e7ff", color: "#3730a3", border: "#a5b4fc" },
  EMERGENCY: { bg: "#fdecea", color: "#842029", border: "#f5c2c7" },
  OT:        { bg: "#ccfbf1", color: "#0f766e", border: "#5eead4" },
};

const STATUS_BADGE = {
  success:   { bg: "#d1f2db", color: "#145a32", border: "#badbcc" },
  warning:   { bg: "#fff8e1", color: "#856404", border: "#ffe69c" },
  danger:    { bg: "#fdecea", color: "#842029", border: "#f5c2c7" },
  info:      { bg: "#dbeafe", color: "#1e40af", border: "#93c5fd" },
  muted:     { bg: "#f1f3f5", color: "#6c757d", border: "#dee2e6" },
  primary:   { bg: "#e0e7ff", color: "#3730a3", border: "#a5b4fc" },
  teal:      { bg: "#ccfbf1", color: "#0f766e", border: "#5eead4" },
};

function Badge({ children, tone = "info" }) {
  const s = STATUS_BADGE[tone] || STATUS_BADGE.info;
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 20,
      fontSize: 10, fontWeight: 700, background: s.bg, color: s.color,
      border: `1px solid ${s.border}`, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

function KpiCard({ label, value, sub, icon, iconBg, valueColor }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
      padding: "16px 18px", display: "flex", alignItems: "center", gap: 14, flex: 1, minWidth: 150,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, background: iconBg,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <SvgIcon name={icon} size={18} color="#fff" />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.textSub, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 3 }}>
          {label}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: valueColor || C.text, lineHeight: 1.1 }}>{value}</div>
        {sub && <div style={{ fontSize: 10, color: C.textSub, marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function SummaryRow({ label, value, bold }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
      <span style={{ color: C.textSub }}>{label}</span>
      <span style={{ fontWeight: bold ? 700 : 600, color: C.text }}>{value}</span>
    </div>
  );
}

function MiniStat({ label, value, tone, bold }) {
  const tones = {
    muted:   { bg: "#f8f9fa", color: C.text },
    info:    { bg: "#e9f5fe", color: "#0d6efd" },
    success: { bg: "#d1f2db", color: "#145a32" },
    warning: { bg: "#fff8e1", color: "#856404" },
  };
  const t = tones[tone] || tones.muted;
  return (
    <div style={{ borderRadius: 8, border: `1px solid ${C.border}`, padding: "10px 12px", background: t.bg }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      <div style={{ marginTop: 4, fontSize: bold ? 18 : 15, fontWeight: bold ? 800 : 700, color: t.color }}>{value}</div>
    </div>
  );
}

const LINE_STATUS_TONE = {
  CALCULATED: "info",
  HOLD: "warning",
  APPROVED: "warning",
  PAID: "success",
  REVERSAL: "danger",
};

// ─── Doctor detail modal ──────────────────────────────────────────────────────
function DoctorPayoutModal({ providerId, period, previewRow, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lineBusy, setLineBusy] = useState("");

  const loadDetail = useCallback(() => {
    if (!providerId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setData(null);
    getProviderPayoutDetail(providerId, { fromDate: period.fromDate, toDate: period.toDate })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message || "Failed to load provider"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [providerId, period.fromDate, period.toDate]);

  useEffect(() => loadDetail(), [loadDetail]);

  const handleLineAction = async (line, action) => {
    if (!line.lineId) { setError("Line id unavailable for this action."); return; }
    setLineBusy(line.id);
    setError("");
    try {
      if (action === "hold") await holdPayoutLine(line.lineId, { remarks: "Held from payout UI" });
      else await releasePayoutLine(line.lineId, { remarks: "Released from payout UI" });
      loadDetail();
    } catch (e) {
      setError(e.message || "Line action failed");
    } finally {
      setLineBusy("");
    }
  };

  if (!providerId) return null;

  const headerDoc = data?.doc || previewRow?.doc;
  const periodLabel = data?.payCycle?.label || period.label;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 16, width: "min(960px, 100%)", maxHeight: "90vh", overflow: "auto", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "22px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: C.text }}>
              {loading && !headerDoc ? "Loading…" : headerDoc?.name || "Doctor payout"}
            </h3>
            {headerDoc && (
              <p style={{ margin: "4px 0 0", fontSize: 12, color: C.textSub }}>
                {headerDoc.specialty} · {headerDoc.empType !== "—" ? `${headerDoc.empType} · ` : ""}{headerDoc.panMasked}
              </p>
            )}
            <p style={{ margin: "6px 0 0", fontSize: 11, color: C.textSub }}>
              Pay cycle · {periodLabel}
            </p>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4 }}>
            <SvgIcon name="x" size={18} color={C.textSub} />
          </button>
        </div>

        {error && (
          <div style={{ margin: "16px 24px", padding: "10px 12px", background: "#fdecea", color: C.danger, borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 48, textAlign: "center", color: C.textSub }}>Loading payout lines…</div>
        ) : data && (
          <>
            <div style={{ padding: "18px 24px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10 }}>
              <MiniStat label="Gross" value={fmtINR(data.gross)} tone="muted" />
              <MiniStat label="Payable" value={fmtINR(data.payable)} tone="info" />
              <MiniStat label="TDS" value={`-${fmtINR(data.tds)}`} tone="warning" />
              <MiniStat label="Net" value={fmtINR(data.net)} tone="info" />
              <MiniStat label="Advances" value={`-${fmtINR(data.advanceTotal)}`} tone="warning" />
              <MiniStat label="Final" value={fmtINR(data.finalPayout)} tone="success" bold />
            </div>

            {(data.mix?.length > 0 || data.linesCount > 0) && (
              <div style={{ padding: "0 24px 14px", display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                {data.mix.map((m) => {
                  const encType = m.encounter_type || m.encounter;
                  return (
                    <Badge key={encType} tone={encType === "OT" ? "teal" : encType === "EMERGENCY" ? "danger" : encType === "IPD" ? "primary" : "info"}>
                      {encType} {fmtINR(Number(m.amount))}
                    </Badge>
                  );
                })}
                {data.linesCount > 0 && (
                  <span style={{ fontSize: 11, color: C.textSub, fontWeight: 600 }}>
                    {data.linesCount} payout line{data.linesCount === 1 ? "" : "s"}
                  </span>
                )}
              </div>
            )}

            {data.doc.advances?.length > 0 && (
              <div style={{ margin: "0 24px 16px", border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
                <div style={{ padding: "10px 14px", background: "#fff8e1", borderBottom: `1px solid ${C.border}`, fontSize: 13, fontWeight: 700, color: "#856404" }}>
                  Advances / Deposits — {fmtINR(data.advanceTotal)}
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#f8f9fa", color: C.textSub, fontSize: 10, textTransform: "uppercase" }}>
                      {["Date", "Mode", "Reference", "Note", "Amount"].map((h) => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: h === "Amount" ? "right" : "left", fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.doc.advances.map((a) => (
                      <tr key={a.id} style={{ borderTop: `1px solid ${C.border}` }}>
                        <td style={{ padding: "8px 12px", color: C.textSub }}>{a.date}</td>
                        <td style={{ padding: "8px 12px" }}><Badge tone="muted">{a.mode}</Badge></td>
                        <td style={{ padding: "8px 12px", fontFamily: "monospace", fontSize: 11 }}>{a.ref}</td>
                        <td style={{ padding: "8px 12px", color: C.textSub }}>{a.note || "—"}</td>
                        <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 700 }}>-{fmtINR(a.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ margin: "0 24px 16px", border: `1px solid ${C.border}`, borderRadius: 10, overflow: "auto", maxHeight: 360 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#f8f9fa", color: C.textSub, fontSize: 10, textTransform: "uppercase", position: "sticky", top: 0 }}>
                    {["Date", "Encounter", "Service", "Rule", "Gross", "Payable", "TDS", "Net", "Status", ""].map((h, i) => (
                      <th key={h || `act-${i}`} style={{ padding: "8px 10px", textAlign: ["Gross", "Payable", "TDS", "Net"].includes(h) ? "right" : "left", fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.lines.length === 0 ? (
                    <tr><td colSpan={10} style={{ padding: 24, textAlign: "center", color: C.textSub }}>No payout lines in this period.</td></tr>
                  ) : data.lines.map((ln) => {
                    const enc = ENC_BADGE[ln.encounter] || STATUS_BADGE.muted;
                    return (
                      <tr key={ln.id} className="payout-row" style={{ borderTop: `1px solid ${C.border}` }}>
                        <td style={{ padding: "8px 10px", color: C.textSub }}>{ln.date}</td>
                        <td style={{ padding: "8px 10px" }}>
                          <span style={{ display: "inline-block", padding: "2px 7px", borderRadius: 20, fontSize: 10, fontWeight: 700, background: enc.bg, color: enc.color, border: `1px solid ${enc.border}` }}>{ln.encounter}</span>
                          <div style={{ fontSize: 9, color: C.textSub, fontFamily: "monospace", marginTop: 2 }}>{ln.encounterId}</div>
                        </td>
                        <td style={{ padding: "8px 10px" }}>
                          <div style={{ fontWeight: 600 }}>{ln.service}</div>
                          <div style={{ fontSize: 10, color: C.textSub }}>{ln.patient} · {ln.code}</div>
                        </td>
                        <td style={{ padding: "8px 10px" }}>
                          {ln.rule ? (
                            <>
                              <div style={{ fontSize: 11, fontWeight: 600 }}>{ln.rule.name}</div>
                              {ln.explain && <div style={{ fontSize: 10, color: C.textSub }}>{ln.explain}</div>}
                              {ln.payoutRole && <div style={{ fontSize: 9, color: C.textSub, marginTop: 2 }}>{ln.payoutRole.replace(/_/g, " ")}</div>}
                            </>
                          ) : (
                            <Badge tone="warning">No rule</Badge>
                          )}
                        </td>
                        <td style={{ padding: "8px 10px", textAlign: "right", color: C.textSub }}>{fmtINR(ln.gross)}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right" }}>{fmtINR(ln.payable)}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right", color: C.textSub }}>-{fmtINR(ln.tds)}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>{fmtINR(ln.net)}</td>
                        <td style={{ padding: "8px 10px" }}>
                          <Badge tone={LINE_STATUS_TONE[ln.status] || "muted"}>{ln.status}</Badge>
                        </td>
                        <td style={{ padding: "8px 10px", textAlign: "right" }}>
                          {(ln.status === "CALCULATED" || ln.status === "HOLD") && (
                            <button
                              onClick={() => handleLineAction(ln, ln.status === "HOLD" ? "release" : "hold")}
                              disabled={lineBusy === ln.id}
                              style={{
                                padding: "4px 9px", borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: lineBusy === ln.id ? "wait" : "pointer",
                                border: `1px solid ${ln.status === "HOLD" ? "#badbcc" : "#ffe69c"}`,
                                background: ln.status === "HOLD" ? "#d1f2db" : "#fff8e1",
                                color: ln.status === "HOLD" ? "#145a32" : "#856404",
                              }}
                            >
                              {lineBusy === ln.id ? "…" : ln.status === "HOLD" ? "Release" : "Hold"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {data.footerNote && (
              <div style={{ margin: "0 24px 16px", padding: "12px 14px", background: "#f8f9fa", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 11, color: C.textSub, lineHeight: 1.5 }}>
                {data.footerNote}
              </div>
            )}

            <div style={{ padding: "16px 24px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end" }}>
              <button onClick={onClose} style={{ padding: "9px 16px", border: `1px solid ${C.border}`, borderRadius: 8, background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", color: C.textSub }}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Generate payout run modal ────────────────────────────────────────────────
function GeneratePayoutRunModal({ open, onClose, period, onComplete }) {
  const [step, setStep] = useState("review");
  const [preview, setPreview] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [approvers, setApprovers] = useState([]);
  const [draft, setDraft] = useState({
    mode: "NEFT",
    scheduleDate: todayISO(1),
    reference: "PAYRUN",
    notifyDoctors: true,
    postToLedger: true,
    approver: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [runResult, setRunResult] = useState(null);
  const [actionBusy, setActionBusy] = useState("");
  const [disbursed, setDisbursed] = useState(false);

  const loadPreview = useCallback(async (providerIds) => {
    setPreviewLoading(true);
    setPreviewError("");
    try {
      const data = await getPayoutRunPreview({
        periodFrom: period.fromDate,
        periodTo: period.toDate,
        providerIds,
      });
      const list = data.providers || [];
      setPreview(list);
      if (!providerIds) {
        setSelected(new Set(list.map((p) => p.doc.id)));
      }
    } catch (e) {
      setPreviewError(e.message || "Failed to load preview");
      setPreview([]);
    } finally {
      setPreviewLoading(false);
    }
  }, [period.fromDate, period.toDate]);

  useEffect(() => {
    if (!open) return;
    setStep("review");
    setRunResult(null);
    setSubmitError("");
    setDisbursed(false);
    loadPreview();
  }, [open, loadPreview]);

  useEffect(() => {
    if (!open || step !== "schedule") return;
    getPayoutApprovers()
      .then((list) => {
        setApprovers(list);
        if (list.length && !draft.approver) {
          setDraft((d) => ({ ...d, approver: list[0].code }));
        }
      })
      .catch(() => setApprovers([]));
  }, [open, step, draft.approver]);

  if (!open) return null;

  const eligible = preview.filter((p) => p.finalPayout > 0 || p.recovery > 0);
  const chosen = preview.filter((p) => selected.has(p.doc.id));
  const sums = chosen.reduce(
    (a, c) => ({
      net: a.net + c.net,
      advances: a.advances + c.advanceTotal,
      tds: a.tds + c.tds,
      final: a.final + c.finalPayout,
      recovery: a.recovery + c.recovery,
    }),
    { net: 0, advances: 0, tds: 0, final: 0, recovery: 0 },
  );
  const toDisburse = chosen.filter((c) => c.finalPayout > 0).length;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const ids = eligible.map((e) => e.doc.id);
    if (selected.size === ids.length) setSelected(new Set());
    else setSelected(new Set(ids));
  };

  const goConfirm = async () => {
    const ids = [...selected];
    await loadPreview(ids);
    setStep("confirm");
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const result = await createPayoutRunWizard(
        buildWizardPayload(draft, period.fromDate, period.toDate, [...selected]),
      );
      setRunResult(result);
      setDisbursed(Boolean(draft.autoDisburse));
      setStep("done");
      onComplete?.();
    } catch (e) {
      setSubmitError(e.message || "Failed to create payout run");
    } finally {
      setSubmitting(false);
    }
  };

  const runId = runResult?.payout_run_id || runResult?.id || runResult?.run_id || "";

  const stepLabels = ["Select doctors", "Schedule & options", "Confirm", "Complete"];
  const stepIdx = { review: 0, schedule: 1, confirm: 2, done: 3 }[step];

  const inputStyle = { width: "100%", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, color: C.text, outline: "none", boxSizing: "border-box", background: "#fff" };
  const labelStyle = { fontSize: 11, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 5 };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 16, width: "min(900px, 100%)", maxHeight: "92vh", overflow: "auto", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "22px 24px", borderBottom: `1px solid ${C.border}` }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: C.text }}>Generate Payout Run</h3>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: C.textSub }}>Pay cycle · {period.label}</p>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            {stepLabels.map((lbl, i) => (
              <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                <div style={{
                  width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 700, border: `1.5px solid ${C.border}`,
                  background: i < stepIdx ? C.success : i === stepIdx ? C.primary : "#fff",
                  color: i <= stepIdx ? "#fff" : C.textSub,
                }}>{i < stepIdx ? "✓" : i + 1}</div>
                <span style={{ fontWeight: i === stepIdx ? 700 : 500, color: i === stepIdx ? C.text : C.textSub }}>{lbl}</span>
                {i < stepLabels.length - 1 && <SvgIcon name="arrow-right" size={12} color={C.textSub} />}
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: "18px 24px" }}>
          {previewError && (
            <div style={{ marginBottom: 12, padding: "10px 12px", background: "#fdecea", color: C.danger, borderRadius: 8, fontSize: 13 }}>{previewError}</div>
          )}
          {submitError && (
            <div style={{ marginBottom: 12, padding: "10px 12px", background: "#fdecea", color: C.danger, borderRadius: 8, fontSize: 13 }}>{submitError}</div>
          )}

          {step === "review" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 16 }}>
                <MiniStat label="Eligible doctors" value={String(eligible.length)} tone="info" />
                <MiniStat label="Selected" value={String(chosen.length)} tone="info" />
                <MiniStat label="Final disbursal" value={fmtINR(sums.final)} tone="success" bold />
                <MiniStat label="To recover" value={fmtINR(sums.recovery)} tone={sums.recovery > 0 ? "warning" : "muted"} />
              </div>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: "auto", maxHeight: 320 }}>
                {previewLoading ? (
                  <div style={{ padding: 32, textAlign: "center", color: C.textSub }}>Loading eligible doctors…</div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: "#f8f9fa", fontSize: 10, textTransform: "uppercase", color: C.textSub }}>
                        <th style={{ padding: "8px 10px", textAlign: "left" }}>
                          <input type="checkbox" checked={eligible.length > 0 && selected.size === eligible.length} onChange={toggleAll} />
                        </th>
                        <th style={{ padding: "8px 10px", textAlign: "left" }}>Doctor</th>
                        <th style={{ padding: "8px 10px", textAlign: "right" }}>Net</th>
                        <th style={{ padding: "8px 10px", textAlign: "right" }}>Advances</th>
                        <th style={{ padding: "8px 10px", textAlign: "right" }}>Final</th>
                        <th style={{ padding: "8px 10px", textAlign: "left" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eligible.map((c) => (
                        <tr key={c.doc.id} style={{ borderTop: `1px solid ${C.border}` }}>
                          <td style={{ padding: "8px 10px" }}><input type="checkbox" checked={selected.has(c.doc.id)} onChange={() => toggle(c.doc.id)} /></td>
                          <td style={{ padding: "8px 10px" }}>
                            <div style={{ fontWeight: 600 }}>{c.doc.name}</div>
                            <div style={{ fontSize: 10, color: C.textSub }}>{c.doc.specialty}</div>
                          </td>
                          <td style={{ padding: "8px 10px", textAlign: "right" }}>{fmtINR(c.net)}</td>
                          <td style={{ padding: "8px 10px", textAlign: "right", color: C.textSub }}>-{fmtINR(c.advanceTotal)}</td>
                          <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>{fmtINR(c.finalPayout)}</td>
                          <td style={{ padding: "8px 10px" }}>
                            {c.recovery > 0 ? <Badge tone="danger">Recover {fmtINR(c.recovery)}</Badge>
                              : c.finalPayout === 0 ? <Badge tone="muted">Zero</Badge>
                              : <Badge tone="success">Ready</Badge>}
                          </td>
                        </tr>
                      ))}
                      {!previewLoading && eligible.length === 0 && (
                        <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: C.textSub }}>No eligible doctors in this period.</td></tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {step === "schedule" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label style={labelStyle}>Payment mode</label>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
                    {["NEFT", "RTGS", "IMPS", "UPI"].map((m) => (
                      <button key={m} type="button" onClick={() => setDraft({ ...draft, mode: m })}
                        style={{
                          padding: "8px 4px", borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: "pointer",
                          border: `1.5px solid ${draft.mode === m ? C.primary : C.border}`,
                          background: draft.mode === m ? C.primary : "#fff",
                          color: draft.mode === m ? "#fff" : C.text,
                        }}>{m}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Scheduled date</label>
                  <input type="date" value={draft.scheduleDate} min={todayISO(0)}
                    onChange={(e) => setDraft({ ...draft, scheduleDate: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Narration prefix</label>
                  <input value={draft.reference} onChange={(e) => setDraft({ ...draft, reference: e.target.value.toUpperCase().slice(0, 10) })} style={{ ...inputStyle, fontFamily: "monospace" }} />
                </div>
                <div>
                  <label style={labelStyle}>Approver</label>
                  <select value={draft.approver} onChange={(e) => setDraft({ ...draft, approver: e.target.value })} style={inputStyle}>
                    {approvers.length === 0 && <option value="">Loading approvers…</option>}
                    {approvers.map((a) => (
                      <option key={a.code} value={a.code}>{a.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label style={labelStyle}>Notes / remarks</label>
                  <textarea value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
                </div>
                <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                  {[
                    { key: "notifyDoctors", label: "Notify doctors by SMS + email", hint: "Sends payslip link on disbursal" },
                    { key: "postToLedger", label: "Post entries to GL ledger", hint: "Debits doctor payable, credits bank suspense" },
                    { key: "autoDisburse", label: "Auto-disburse on generate", hint: "Marks the run PAID immediately instead of leaving it APPROVED" },
                  ].map(({ key, label, hint }) => (
                    <label key={key} style={{ display: "flex", gap: 10, cursor: "pointer", alignItems: "flex-start" }}>
                      <input type="checkbox" checked={draft[key]} onChange={(e) => setDraft({ ...draft, [key]: e.target.checked })} style={{ marginTop: 3 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
                        <div style={{ fontSize: 11, color: C.textSub }}>{hint}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === "confirm" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ padding: 20, borderRadius: 12, border: `1px solid ${C.border}`, background: C.accent }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textSub, textTransform: "uppercase" }}>You are about to disburse</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: C.text, marginTop: 4 }}>{fmtINR(sums.final)}</div>
                <div style={{ fontSize: 12, color: C.textSub, marginTop: 4 }}>
                  to {toDisburse} doctor{toDisburse === 1 ? "" : "s"} via {draft.mode} on {draft.scheduleDate}
                </div>
              </div>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
                <SummaryRow label="Pay cycle" value={period.label} />
                <SummaryRow label="Payment mode" value={draft.mode} />
                <SummaryRow label="Scheduled date" value={draft.scheduleDate} />
                <SummaryRow label="Approver" value={draft.approver || "—"} />
                <SummaryRow label="Doctors selected" value={String(chosen.length)} />
              </div>
            </div>
          )}

          {step === "done" && (
            <div style={{ textAlign: "center", padding: "12px 0" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.success, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}>
                <SvgIcon name="check" size={28} color="#fff" />
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, marginTop: 14, color: C.text }}>Payout run created</div>
              <div style={{ fontSize: 13, color: C.textSub, marginTop: 4 }}>
                {toDisburse} doctor{toDisburse === 1 ? "" : "s"} · {fmtINR(sums.final)} scheduled for {draft.scheduleDate}
              </div>
              {runId && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 12, padding: "8px 14px", border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: "monospace", fontSize: 14, fontWeight: 700 }}>
                  {runId}
                  <Badge tone={disbursed ? "success" : "warning"}>{disbursed ? "PAID" : "APPROVED"}</Badge>
                </div>
              )}
              {runId && !disbursed && (
                <div style={{ marginTop: 16 }}>
                  <button type="button" disabled={actionBusy === "disburse"} onClick={async () => {
                    setActionBusy("disburse");
                    try {
                      await disbursePayoutRun(runId, {
                        payment_mode: draft.mode,
                        narration_prefix: draft.reference,
                        disbursed_by: draft.approver,
                        remarks: draft.notes,
                        providers: chosen,
                      });
                      setDisbursed(true);
                      onComplete?.();
                    } catch (e) { alert(e.message); } finally { setActionBusy(""); }
                  }} style={{ padding: "10px 20px", border: "none", borderRadius: 8, background: C.success, fontSize: 13, fontWeight: 700, cursor: actionBusy === "disburse" ? "wait" : "pointer", color: "#fff" }}>
                    {actionBusy === "disburse" ? "Disbursing…" : `Disburse now · ${fmtINR(sums.final)}`}
                  </button>
                </div>
              )}
              {runId && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 20, textAlign: "left" }}>
                  <button type="button" disabled={actionBusy === "bank"} onClick={async () => {
                    setActionBusy("bank");
                    try { await downloadPayoutBankFile(runId); } catch (e) { alert(e.message); } finally { setActionBusy(""); }
                  }} style={{ padding: 12, border: `1px solid ${C.border}`, borderRadius: 8, background: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    {actionBusy === "bank" ? "Downloading…" : "Download bank CSV"}
                  </button>
                  <button type="button" disabled={actionBusy === "tds"} onClick={async () => {
                    setActionBusy("tds");
                    try { await downloadPayoutTdsRegister(runId); } catch (e) { alert(e.message); } finally { setActionBusy(""); }
                  }} style={{ padding: 12, border: `1px solid ${C.border}`, borderRadius: 8, background: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    {actionBusy === "tds" ? "Downloading…" : "TDS register"}
                  </button>
                  <button type="button" disabled={actionBusy === "payslip"} onClick={async () => {
                    setActionBusy("payslip");
                    try {
                      const r = await generatePayoutPayslips(runId);
                      alert(r.message || `Generated ${r.count ?? ""} payslip(s)`);
                    } catch (e) { alert(e.message); } finally { setActionBusy(""); }
                  }} style={{ padding: 12, border: `1px solid ${C.border}`, borderRadius: 8, background: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    {actionBusy === "payslip" ? "Generating…" : "Generate payslips"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: "16px 24px", borderTop: `1px solid ${C.border}`, display: "flex", gap: 10, justifyContent: "flex-end" }}>
          {step === "review" && (
            <>
              <button type="button" onClick={onClose} style={{ padding: "9px 16px", border: `1px solid ${C.border}`, borderRadius: 8, background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button type="button" onClick={() => setStep("schedule")} disabled={chosen.length === 0 || previewLoading}
                style={{ padding: "9px 16px", border: "none", borderRadius: 8, background: C.primary, fontSize: 13, fontWeight: 700, cursor: "pointer", color: "#fff", opacity: chosen.length === 0 ? 0.5 : 1 }}
              >Next · Schedule</button>
            </>
          )}
          {step === "schedule" && (
            <>
              <button type="button" onClick={() => setStep("review")} style={{ padding: "9px 16px", border: `1px solid ${C.border}`, borderRadius: 8, background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Back</button>
              <button type="button" onClick={goConfirm} disabled={!draft.approver && approvers.length > 0}
                style={{ padding: "9px 16px", border: "none", borderRadius: 8, background: C.primary, fontSize: 13, fontWeight: 700, cursor: "pointer", color: "#fff" }}
              >Next · Confirm</button>
            </>
          )}
          {step === "confirm" && (
            <>
              <button type="button" onClick={() => setStep("schedule")} disabled={submitting} style={{ padding: "9px 16px", border: `1px solid ${C.border}`, borderRadius: 8, background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Back</button>
              <button type="button" onClick={submit} disabled={submitting}
                style={{ padding: "9px 16px", border: "none", borderRadius: 8, background: C.primary, fontSize: 13, fontWeight: 700, cursor: submitting ? "wait" : "pointer", color: "#fff" }}
              >{submitting ? "Generating…" : "Generate run"}</button>
            </>
          )}
          {step === "done" && (
            <button type="button" onClick={onClose} style={{ padding: "9px 16px", border: "none", borderRadius: 8, background: C.primary, fontSize: 13, fontWeight: 700, cursor: "pointer", color: "#fff" }}>Done</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Payout run detail modal ──────────────────────────────────────────────────
function RunDetailModal({ runId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    if (!runId) return;
    setLoading(true);
    setError("");
    getPayoutRunDetail(runId)
      .then(setData)
      .catch((e) => setError(e.message || "Failed to load run"))
      .finally(() => setLoading(false));
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  if (!runId) return null;

  const status = data?.status || "";
  const canApprove = ["GENERATED", "CALCULATED", "DRAFT"].includes(status);
  const canDisburse = status === "APPROVED";

  const runAction = async (kind) => {
    setBusy(kind);
    setError("");
    try {
      if (kind === "approve") await approvePayoutRun(runId);
      else if (kind === "disburse") {
        await disbursePayoutRun(runId);
      }
      else if (kind === "bank") { await downloadPayoutBankFile(runId); setBusy(""); return; }
      else if (kind === "tds") { await downloadPayoutTdsRegister(runId); setBusy(""); return; }
      else if (kind === "payslip") {
        const r = await generatePayoutPayslips(runId);
        alert(r.message || `Generated ${r.count ?? ""} payslip(s)`);
        setBusy("");
        return;
      }
      load();
      onChanged?.();
    } catch (e) {
      setError(e.message || "Action failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 16, width: "min(900px, 100%)", maxHeight: "90vh", overflow: "auto", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "22px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: C.text }}>
              {loading ? "Loading…" : `Payout run ${data?.runNumber || ""}`}
            </h3>
            {data && (
              <p style={{ margin: "4px 0 0", fontSize: 12, color: C.textSub }}>
                {data.period} · {data.paymentMode} · {data.doctors} doctor{data.doctors === 1 ? "" : "s"}
              </p>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {data && <Badge tone={RUN_STATUS_TONE[status] || "muted"}>{status}</Badge>}
            <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4 }}>
              <SvgIcon name="x" size={18} color={C.textSub} />
            </button>
          </div>
        </div>

        {error && (
          <div style={{ margin: "16px 24px", padding: "10px 12px", background: "#fdecea", color: C.danger, borderRadius: 8, fontSize: 13, fontWeight: 600 }}>{error}</div>
        )}

        {loading ? (
          <div style={{ padding: 48, textAlign: "center", color: C.textSub }}>Loading run detail…</div>
        ) : data && (
          <>
            <div style={{ padding: "18px 24px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10 }}>
              <MiniStat label="Gross" value={fmtINR(data.summary.gross)} tone="muted" />
              <MiniStat label="Payable" value={fmtINR(data.summary.payable)} tone="info" />
              <MiniStat label="TDS" value={`-${fmtINR(data.summary.tds)}`} tone="warning" />
              <MiniStat label="Net" value={fmtINR(data.summary.net)} tone="info" />
              <MiniStat label="Final" value={fmtINR(data.summary.finalPayout)} tone="success" bold />
            </div>

            <div style={{ margin: "0 24px 16px", border: `1px solid ${C.border}`, borderRadius: 10, overflow: "auto", maxHeight: 320 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#f8f9fa", color: C.textSub, fontSize: 10, textTransform: "uppercase", position: "sticky", top: 0 }}>
                    {["Doctor", "Lines", "Net", "TDS", "Final", "Status"].map((h) => (
                      <th key={h} style={{ padding: "8px 12px", textAlign: ["Lines", "Net", "TDS", "Final"].includes(h) ? "right" : "left", fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.providers.length === 0 ? (
                    <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: C.textSub }}>No providers on this run.</td></tr>
                  ) : data.providers.map((p) => (
                    <tr key={p.providerId || p.doc.id} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={{ padding: "8px 12px" }}>
                        <div style={{ fontWeight: 600 }}>{p.doc.name}</div>
                        <div style={{ fontSize: 10, color: C.textSub }}>{p.doc.specialty}</div>
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "right" }}>{p.linesCount}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right" }}>{fmtINR(p.net)}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right", color: C.textSub }}>-{fmtINR(p.tds)}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 700 }}>{fmtINR(p.finalPayout)}</td>
                      <td style={{ padding: "8px 12px" }}><Badge tone={RUN_STATUS_TONE[p.status] || "muted"}>{p.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ padding: "16px 24px", borderTop: `1px solid ${C.border}`, display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button type="button" disabled={busy === "bank"} onClick={() => runAction("bank")} style={{ padding: "9px 14px", border: `1px solid ${C.border}`, borderRadius: 8, background: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                {busy === "bank" ? "…" : "Bank CSV"}
              </button>
              <button type="button" disabled={busy === "tds"} onClick={() => runAction("tds")} style={{ padding: "9px 14px", border: `1px solid ${C.border}`, borderRadius: 8, background: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                {busy === "tds" ? "…" : "TDS register"}
              </button>
              <button type="button" disabled={busy === "payslip"} onClick={() => runAction("payslip")} style={{ padding: "9px 14px", border: `1px solid ${C.border}`, borderRadius: 8, background: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                {busy === "payslip" ? "…" : "Payslips"}
              </button>
              {canApprove && (
                <button type="button" disabled={busy === "approve"} onClick={() => runAction("approve")} style={{ padding: "9px 16px", border: "none", borderRadius: 8, background: C.warning, fontSize: 13, fontWeight: 700, cursor: "pointer", color: "#856404" }}>
                  {busy === "approve" ? "Approving…" : "Approve"}
                </button>
              )}
              {canDisburse && (
                <button type="button" disabled={busy === "disburse"} onClick={() => runAction("disburse")} style={{ padding: "9px 16px", border: "none", borderRadius: 8, background: C.success, fontSize: 13, fontWeight: 700, cursor: "pointer", color: "#fff" }}>
                  {busy === "disburse" ? "Disbursing…" : `Disburse · ${fmtINR(data.summary.finalPayout)}`}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── main page ────────────────────────────────────────────────────────────────
export default function IPDBillingPayouts() {
  const history = useHistory();
  const defaultPeriod = defaultPayPeriod();
  const [fromDate, setFromDate] = useState(defaultPeriod.fromDate);
  const [toDate, setToDate] = useState(defaultPeriod.toDate);
  const [periodLabel, setPeriodLabel] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [enc, setEnc] = useState(() => new Set(ENC_OPTIONS));
  const [detailProviderId, setDetailProviderId] = useState(null);
  const [detailPreviewRow, setDetailPreviewRow] = useState(null);
  const [runOpen, setRunOpen] = useState(false);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [detailRunId, setDetailRunId] = useState(null);

  const [cards, setCards] = useState(null);
  const [disbursal, setDisbursal] = useState(null);
  const [register, setRegister] = useState([]);
  const [insights, setInsights] = useState([]);
  const [activeRules, setActiveRules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const searchTimer = useRef(null);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(searchTimer.current);
  }, [searchInput]);

  const period = { fromDate, toDate, label: periodLabel || formatPayCycleLabel(fromDate, toDate) };
  const encounterTypes = ENC_OPTIONS.filter((e) => enc.has(e));
  const filters = { fromDate, toDate, search, encounterTypes };

  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      // Optional best-effort prerequisite — never blocks the main dashboard call
      await calculateDoctorPayouts({ fromDate, toDate });

      // Main screen endpoint (payout-ui.md): GET /doctor-payouts/dashboard/
      const dashboard = await getPayoutDashboard(filters);
      setCards(dashboard.cards);
      setDisbursal(dashboard.disbursal);
      setRegister(dashboard.register);
      setPeriodLabel(formatPayCycleLabel(
        dashboard.payCycle.fromDate || fromDate,
        dashboard.payCycle.toDate || toDate,
        dashboard.payCycle.label,
      ));

      // Sidebar extras — independent so their failure can't blank the page
      getPayoutInsights({ fromDate, toDate })
        .then(setInsights)
        .catch(() => setInsights([]));
      getActivePayoutRules()
        .then(setActiveRules)
        .catch(() => setActiveRules([]));
    } catch (e) {
      setError(e.message || "Failed to load payout data");
      setRegister([]);
      setCards(null);
      setDisbursal(null);
    } finally {
      setIsLoading(false);
    }
  }, [fromDate, toDate, search, encounterTypes.join(",")]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const loadRuns = useCallback(() => {
    setRunsLoading(true);
    listPayoutRuns({ period_from: fromDate, period_to: toDate })
      .then(setRuns)
      .catch(() => setRuns([]))
      .finally(() => setRunsLoading(false));
  }, [fromDate, toDate]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const refreshAll = useCallback(() => {
    loadDashboard();
    loadRuns();
  }, [loadDashboard, loadRuns]);

  const toggleEnc = (e) => {
    setEnc((prev) => {
      const next = new Set(prev);
      if (next.has(e)) next.delete(e);
      else next.add(e);
      if (next.size === 0) next.add(e);
      return next;
    });
  };

  const openProviderDetail = (row) => {
    setDetailProviderId(row.providerId || row.doc?.id);
    setDetailPreviewRow(row);
  };

  const closeProviderDetail = () => {
    setDetailProviderId(null);
    setDetailPreviewRow(null);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportPayoutRegister(filters);
    } catch (e) {
      alert(e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const totals = disbursal || cards || {
    doctors: 0, gross: 0, payable: 0, tds: 0, net: 0, advances: 0, finalPayout: 0, recovery: 0,
  };

  const btnSecondary = {
    display: "inline-flex", alignItems: "center", gap: 7,
    padding: "8px 16px", border: `1.5px solid ${C.primary}`, borderRadius: 8,
    background: "transparent", color: C.primary, fontSize: 13, fontWeight: 600, cursor: "pointer",
  };
  const btnPrimary = {
    display: "inline-flex", alignItems: "center", gap: 7,
    padding: "8px 16px", border: "none", borderRadius: 8,
    background: C.primary, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
  };
  const dateInputStyle = {
    padding: "7px 10px", border: `1px solid ${C.border}`, borderRadius: 8,
    fontSize: 13, color: C.text, background: "#fff",
  };

  return (
    <IPDBillingLayout activeNav="payouts">
      <style>{`
        .payout-row:hover { background: #f8f9fa !important; }
        .payout-enc-btn:hover { filter: brightness(0.95); }
        .payout-export:hover { background: ${C.primary} !important; color: #fff !important; }
        .payout-view:hover { background: ${C.accent} !important; }
      `}</style>

      <div style={{ padding: "28px 32px", display: "flex", flexDirection: "column", gap: 24 }}>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>Doctor Payouts</h2>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: C.textSub }}>
              Pay cycle · {period.label} · Calculated from Charge Capture × Pricing Rules
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={dateInputStyle} />
            <span style={{ color: C.textSub, fontSize: 13 }}>to</span>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={dateInputStyle} />
            <button type="button" className="payout-export" onClick={handleExport} disabled={exporting || isLoading} style={{ ...btnSecondary, opacity: exporting ? 0.6 : 1 }}>
              <SvgIcon name="export" size={15} color="inherit" /> {exporting ? "Exporting…" : "Export"}
            </button>
            <button type="button" onClick={() => setRunOpen(true)} disabled={isLoading} style={btnPrimary}>
              <SvgIcon name="check" size={15} color="#fff" /> Generate Payout Run
            </button>
          </div>
        </div>

        {error && (
          <div style={{ padding: "12px 16px", background: "#fdecea", border: `1px solid #f5c2c7`, borderRadius: 10, color: C.danger, fontSize: 13, fontWeight: 600 }}>
            {error}
          </div>
        )}

        {isLoading ? (
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} style={{ flex: 1, minWidth: 150, height: 80, borderRadius: 10, background: "linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%)", backgroundSize: "200% 100%", animation: "ipdShimmer 1.4s infinite" }} />
            ))}
          </div>
        ) : (
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <KpiCard label="Doctors in cycle" value={String(totals.doctors || 0)} sub="With payable lines" icon="users" iconBg={C.primary} />
            <KpiCard label="Gross billed" value={fmtINR(totals.gross)} sub="From charge capture" icon="bill" iconBg="#0f766e" />
            <KpiCard label="Payable (pre-TDS)" value={fmtINR(totals.payable)} sub="After pricing rules" icon="percent" iconBg={C.success} />
            <KpiCard label="Net after TDS" value={fmtINR(totals.net)} sub={`TDS ${fmtINR(totals.tds)}`} icon="wallet" iconBg={C.success} />
            <KpiCard label="Advances paid" value={fmtINR(totals.advances)} sub="Deposits already given" icon="cash" iconBg={C.warning} valueColor="#856404" />
            <KpiCard label="Final payout" value={fmtINR(totals.finalPayout)} sub={totals.recovery > 0 ? `Recover ${fmtINR(totals.recovery)}` : "Ready to disburse"} icon="check" iconBg={totals.recovery > 0 ? C.danger : C.success} />
          </div>
        )}

        <AdmissionPayoutWorkflow
          onPayoutUpdated={refreshAll}
          payoutDoctors={register.map((r) => ({
            id: r.providerId || r.doc?.id,
            name: r.doc?.name,
            specialty: r.doc?.specialty,
          }))}
        />

        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f8f9fa", border: `1px solid ${C.border}`, borderRadius: 8, padding: "7px 12px", flex: 1, minWidth: 220 }}>
            <SvgIcon name="search" size={15} color={C.textSub} />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search doctor or specialty…"
              style={{ border: "none", outline: "none", background: "transparent", fontSize: 13, width: "100%", color: C.text }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.textSub, fontWeight: 600 }}>
            <SvgIcon name="filter" size={14} color={C.textSub} /> Encounter
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {ENC_OPTIONS.map((e) => {
              const active = enc.has(e);
              return (
                <button key={e} type="button" onClick={() => toggleEnc(e)} className="payout-enc-btn"
                  style={{
                    padding: "5px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer",
                    border: `1.5px solid ${active ? C.primary : C.border}`,
                    background: active ? C.primary : "#fff",
                    color: active ? "#fff" : C.text,
                  }}
                >{e}</button>
              );
            })}
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.textSub }}>
            <SvgIcon name="shield" size={14} color={C.success} />
            {activeRules.length} active payout rules
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 20, alignItems: "start" }}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
            <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Payout register — {register.length} doctors</span>
              <Badge tone="info">{period.label}</Badge>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#f8f9fa", color: C.textSub, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    {["Doctor", "Specialty", "Mix", "Lines", "Gross", "Payable", "TDS", "Net", "Advances", "Final", ""].map((h) => (
                      <th key={h || "action"} style={{ padding: "10px 12px", textAlign: ["Lines", "Gross", "Payable", "TDS", "Net", "Advances", "Final"].includes(h) ? "right" : "left", fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr><td colSpan={11} style={{ padding: 40, textAlign: "center", color: C.textSub }}>Loading payout data…</td></tr>
                  ) : register.length === 0 ? (
                    <tr><td colSpan={11} style={{ padding: 40, textAlign: "center", color: C.textSub }}>No doctors match the current filters.</td></tr>
                  ) : register.map((c) => (
                    <tr key={c.doc.id} className="payout-row" style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ fontWeight: 700 }}>{c.doc.name}</div>
                        <div style={{ fontSize: 10, color: C.textSub }}>{c.doc.empType} · {c.doc.panMasked}</div>
                      </td>
                      <td style={{ padding: "10px 12px", color: C.textSub }}>{c.doc.specialty}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {(c.mix || []).map((m) => {
                            const encType = m.encounter_type || m.encounter;
                            return (
                              <Badge key={encType} tone={encType === "OT" ? "teal" : encType === "EMERGENCY" ? "danger" : encType === "IPD" ? "primary" : "info"}>
                                {encType} {fmtINR(Number(m.amount))}
                              </Badge>
                            );
                          })}
                          {c.unmatched > 0 && <Badge tone="warning">{c.unmatched} unmatched</Badge>}
                        </div>
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "right" }}>{c.linesCount}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right", color: C.textSub }}>{fmtINR(c.gross)}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right" }}>{fmtINR(c.payable)}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right", color: C.textSub }}>-{fmtINR(c.tds)}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right" }}>{fmtINR(c.net)}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right", color: C.textSub }}>
                        {c.advanceTotal > 0 ? `-${fmtINR(c.advanceTotal)}` : "—"}
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 800 }}>
                        {fmtINR(c.finalPayout)}
                        {c.recovery > 0 && <div style={{ fontSize: 10, color: C.danger, fontWeight: 600 }}>Recover {fmtINR(c.recovery)}</div>}
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "center" }}>
                        <button
                          type="button"
                          className="payout-view"
                          title="View payout lines"
                          aria-label={`View payout for ${c.doc.name}`}
                          onClick={() => openProviderDetail(c)}
                          style={{
                            padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: 6,
                            background: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center",
                          }}
                        >
                          <SvgIcon name="eye" size={15} color={C.primary} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18, position: "sticky", top: 80 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>Final disbursal</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: C.text, marginTop: 4 }}>{fmtINR(totals.finalPayout)}</div>
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
                <SummaryRow label="Gross billed" value={fmtINR(totals.gross)} />
                <SummaryRow label="Payable (rules)" value={fmtINR(totals.payable)} />
                <SummaryRow label="TDS" value={`- ${fmtINR(totals.tds)}`} />
                <SummaryRow label="Net after TDS" value={fmtINR(totals.net)} />
                <SummaryRow label="Less: Advances paid" value={`- ${fmtINR(totals.advances)}`} />
                <div style={{ height: 1, background: C.border, margin: "4px 0" }} />
                <SummaryRow label="Net to disburse" value={fmtINR(totals.finalPayout)} bold />
                {totals.recovery > 0 && <SummaryRow label="To recover" value={fmtINR(totals.recovery)} />}
              </div>
              {insights.length > 0 && (
                <div style={{ marginTop: 16, padding: 12, background: "#f8f9fa", borderRadius: 8, border: `1px solid ${C.border}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: C.text }}>
                    <SvgIcon name="sparkle" size={14} color="#0f766e" /> Insights
                  </div>
                  <ul style={{ margin: "8px 0 0", padding: "0 0 0 16px", fontSize: 11, color: C.textSub, lineHeight: 1.6 }}>
                    {insights.map((item, i) => (
                      <li key={i}>{typeof item === "string" ? item : item.text || item.message}</li>
                    ))}
                  </ul>
                </div>
              )}
              <button type="button" onClick={() => history.push("/ipd-billing/pricing")}
                style={{ marginTop: 14, width: "100%", padding: "9px", border: `1px solid ${C.border}`, borderRadius: 8, background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", color: C.primary, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
              >
                <SvgIcon name="percent" size={15} color={C.primary} /> Configure payout rules
              </button>
            </div>

            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
              <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, fontWeight: 700, fontSize: 14, color: C.text }}>Active payout rules</div>
              {activeRules.length === 0 ? (
                <div style={{ padding: 16, fontSize: 12, color: C.textSub }}>No active payout rules found.</div>
              ) : (
                <ul style={{ listStyle: "none", margin: 0, padding: "8px 12px 12px" }}>
                  {activeRules.map((r) => (
                    <li key={r.id} style={{ padding: "10px 12px", border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 8, background: "#fafbfc" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{r.name}</span>
                        <Badge tone={r.kind === "percent" ? "info" : r.kind === "tier" ? "teal" : "primary"}>{r.label}</Badge>
                      </div>
                      <div style={{ fontSize: 10, color: C.textSub, marginTop: 4 }}>
                        {r.summary || [r.encounter, r.category, r.tdsPct != null ? `TDS ${r.tdsPct}%` : null].filter(Boolean).join(" · ") || r.code}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Payout runs — {runs.length}</span>
            <button type="button" onClick={loadRuns} disabled={runsLoading} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", border: `1px solid ${C.border}`, borderRadius: 8, background: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", color: C.textSub }}>
              <SvgIcon name="refresh" size={13} color={C.textSub} /> {runsLoading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#f8f9fa", color: C.textSub, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {["Run #", "Period", "Mode", "Scheduled", "Doctors", "TDS", "Net payout", "Status", ""].map((h) => (
                    <th key={h || "action"} style={{ padding: "10px 12px", textAlign: ["Doctors", "TDS", "Net payout"].includes(h) ? "right" : "left", fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {runsLoading ? (
                  <tr><td colSpan={9} style={{ padding: 32, textAlign: "center", color: C.textSub }}>Loading payout runs…</td></tr>
                ) : runs.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding: 32, textAlign: "center", color: C.textSub }}>No payout runs for this period yet.</td></tr>
                ) : runs.map((r) => (
                  <tr key={r.id} className="payout-row" style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={{ padding: "10px 12px", fontFamily: "monospace", fontWeight: 700 }}>{r.runNumber}</td>
                    <td style={{ padding: "10px 12px", color: C.textSub }}>{r.period}</td>
                    <td style={{ padding: "10px 12px" }}>{r.paymentMode}</td>
                    <td style={{ padding: "10px 12px", color: C.textSub }}>{r.scheduledDate}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right" }}>{r.doctors}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", color: C.textSub }}>-{fmtINR(r.tds)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 800 }}>{fmtINR(r.total)}</td>
                    <td style={{ padding: "10px 12px" }}><Badge tone={RUN_STATUS_TONE[r.status] || "muted"}>{r.status}</Badge></td>
                    <td style={{ padding: "10px 12px" }}>
                      <button type="button" className="payout-view" onClick={() => setDetailRunId(r.id)}
                        style={{ padding: "5px 10px", border: `1px solid ${C.border}`, borderRadius: 6, background: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer", color: C.primary }}
                      >View →</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <DoctorPayoutModal
        providerId={detailProviderId}
        period={{ fromDate, toDate, label: period.label }}
        previewRow={detailPreviewRow}
        onClose={closeProviderDetail}
      />
      <GeneratePayoutRunModal
        open={runOpen}
        onClose={() => setRunOpen(false)}
        period={period}
        onComplete={refreshAll}
      />
      <RunDetailModal
        runId={detailRunId}
        onClose={() => setDetailRunId(null)}
        onChanged={refreshAll}
      />
    </IPDBillingLayout>
  );
}
